import React, { useState } from 'react';
import Card from '../../components/card';
import Title from '../../components/title';
import {
  IOrder,
  ISearchOrderReq,
  ISearchOrderRes,
} from '../../interfaces/order';
import { Button, DatePicker, Input, Select, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { searchOrders } from '../../adapters/order';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import OrderTable from './components/order-table';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getOrderStatusOptions } from '@/helpers/orderHelper';
import { isFullAccess } from '@/utils/permissionUtils';
import { AxiosResponse } from 'axios';
import { EOrderType } from '@/enums/EOrder';

const { Search } = Input;

const _criteria: ISearchOrderReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  orderMainType: EOrderType.EXPORT,
};

const Order = () => {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState<ISearchOrderReq>({
    ..._criteria,
  });

  const { data, refetch, isLoading, isFetching } = useQuery<
    AxiosResponse<ISearchOrderRes>,
    Error
  >({
    queryKey: ['search-order', criteria],
    queryFn: async () => await searchOrders(criteria),
  });

  const onActionOrder = () => {
    refetch();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IOrder) || newCriteria.orderBy;
      newCriteria.orderType = sorter.order || newCriteria.orderType;
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: 'descend',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: 'descend',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };

  const perFullAccess = isFullAccess();

  return (
    <Card>
      <Title title="Export">
        <div className="flex justify-end gap-3">
          {perFullAccess && (
            <Button type="primary" onClick={() => navigate(`/export/create`)}>
              <PlusOutlined /> เพิ่มใหม่
            </Button>
          )}
        </div>
      </Title>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
            />
            <Select
              allowClear
              className="md:min-w-[150px]"
              size="large"
              placeholder="เลือก สถานะ"
              options={getOrderStatusOptions()}
              value={criteria?.status}
              onChange={e =>
                setCriteria({
                  ...criteria,
                  status: e,
                })
              }
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
        </div>
        <OrderTable
          criteria={criteria}
          order={data?.data || { data: [], totalRecords: 0 }}
          loading={isLoading || isFetching}
          onEditData={item => console.log(item)}
          onDelete={onActionOrder}
          onTableChange={onTableChange}
        />
      </div>
    </Card>
  );
};

export default Order;
